import { ReactNode } from 'react';
import {
  AppBar,
  ExtendedTheme,
  Footer,
  UserDetails,
  UserInterfaceUserCircle,
  UserInterfaceConversation,
  useTheme
} from 'neon-design-system';
import { makeStyles, createStyles } from '@material-ui/styles';
import { useAuth0, useAuth0Data } from 'neon-auth0';
import jwt from 'jwt-decode';
import { config } from '../../config';
import { usePageBreadcrumbs } from '../../hooks/usePageBreadcrumbs';
import { PageBreadcrumbs } from '../PageBreadcrumbs/PageBreadcrumbs';
import { Grid, useMediaQuery } from '@material-ui/core';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { baseUrls } from '../../constants/urls';
import { usePermissions } from '../../providers/PermissionsProvider';

interface Props {
  children: ReactNode;
  appVersion: string;
  userDetails: UserDetails;
}

const useStyles = makeStyles((theme: ExtendedTheme) =>
  createStyles({
    root: {
      display: 'flex',
      minHeight: '100vh',
      flexDirection: 'column'
    },
    content: {
      display: 'flex',
      flex: 1
    },
    layoutContainer: {
      flex: 1,
      padding: theme.spacing(0, 2),
      margin: '0 auto',
      minWidth: 0,
      [theme.breakpoints.up('md')]: {
        maxWidth: 1200
      },
      [theme.breakpoints.up('lg')]: {
        padding: 0
      },
      [theme.breakpoints.up('xl')]: {
        maxWidth: 1440
      }
    },
    layoutContainerWide: {
      flex: 1,
      padding: theme.spacing(0, 2),
      margin: '0 auto',
      minWidth: 0,
      [theme.breakpoints.up('md')]: {
        maxWidth: 1640
      }
    },
    breadcrumbsWrapper: {
      padding: 0
    },
    pageWithBreadcrumbs: {
      padding: 0,
      height: '100%'
    }
  })
);

export const Layout = ({ children, appVersion, userDetails }: Props) => {
  const classes = useStyles();
  const { logout } = useAuth0();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const breadcrumbs = usePageBreadcrumbs();
  const { accessToken } = useAuth0Data();
  const isHomePage = useRouteMatch(baseUrls.home)?.isExact;
  const isCashMovementNewRequestSubmittedPage = useRouteMatch(
    baseUrls.cashMovementNewRequestSubmitted
  )?.isExact;
  const isCashMovementBulkSubmittedPage = useRouteMatch(
    baseUrls.cashMovementBulkSubmitted
  )?.isExact;
  const isCreateCashWithdrawalPage = useRouteMatch(baseUrls.cashMovementNewRequest)?.isExact;
  const permissions = usePermissions();
  const getHideBreadcrumbs = () => {
    const urlsToCheck = [
      { url: baseUrls.accountChangeRequests, permissionName: 'accountChangeRequestEnabled' },
      { url: baseUrls.riskLimitsRequests, permissionName: 'riskLimitRequestEnabled' },
      { url: baseUrls.kybRequests, permissionName: 'accountOpeningFormEnabled' },
      { url: baseUrls.generalRequests, permissionName: 'generalRequestsEnabled' },
      { url: baseUrls.cashMovementRequests, permissionName: 'cashMovementRequestsEnabled' }
    ];

    return !!urlsToCheck.find(
      ({ url, permissionName }) =>
        pathname.startsWith(url) && !permissions[permissionName as keyof typeof permissions]
    );
  };

  const showDivider =
    breadcrumbs.length > 2 &&
    !(
      pathname.startsWith(baseUrls.generalRequests) ||
      pathname.startsWith(baseUrls.cashMovementRequests)
    );

  const hideBreadcrumbs =
    getHideBreadcrumbs() ||
    isHomePage ||
    isCashMovementNewRequestSubmittedPage ||
    isCashMovementBulkSubmittedPage;
  const { theme } = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const decodedToken: any = jwt(accessToken);

  const navBarItems = [
    { name: 'Dashboard', link: config.loginPageUrl, ignoreRouter: true },
    ...(decodedToken.permissions?.includes('neon-ai')
      ? [{ name: 'Ai', link: config.aiPageUrl, ignoreRouter: true }]
      : []),
    { name: 'Support Center', link: '/' },
    { name: 'Account settings', link: config.profilePageUrl, ignoreRouter: true }
  ];

  return (
    <div className={classes.root}>
      <AppBar
        app="Neon"
        marexSpectronAppTitle="Client Base"
        showAppSwitcher
        menuItems={navBarItems}
        accountMenuItems={[
          {
            name: 'Manage account',
            icon: <UserInterfaceUserCircle />,
            onClick: () => {
              window.location.href = config.profilePageUrl;
            }
          },
          {
            name: 'Support Center',
            icon: <UserInterfaceConversation />,
            onClick: () => push(baseUrls.home)
          }
        ]}
        userDetails={userDetails}
        appVersion={appVersion}
        onSignOut={() => logout({ returnTo: window.location.origin })}
        homePageLink={config.loginPageUrl}
      />
      <div className={classes.content}>
        <div
          className={
            isCreateCashWithdrawalPage ? classes.layoutContainerWide : classes.layoutContainer
          }
        >
          {!hideBreadcrumbs ? (
            <Grid container spacing={2} className={classes.pageWithBreadcrumbs}>
              {!isMobile && (
                <Grid item xs={12} className={classes.breadcrumbsWrapper}>
                  <PageBreadcrumbs items={breadcrumbs} withBorder={showDivider} />
                </Grid>
              )}
              <Grid item xs={12} className={classes.pageWithBreadcrumbs}>
                {children}
              </Grid>
            </Grid>
          ) : (
            children
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};
